import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { FloatingData, UseFloatProps } from './types';

import debounce from '@snowit/utils/debounce';

import { computePosition } from './utils';

import { flushSync } from 'react-dom';

const useFloat = (props: UseFloatProps = {}) => {
  const { strategy = 'click', time = 10, offset, bindToBody = false } = props;
  const [data, setData] = useState<FloatingData>({
    x: null,
    y: null,
    strategy: strategy,
  });

  const parent = useRef<HTMLElement | null>(null);
  const floating = useRef<HTMLElement | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const isMounted = useRef(false);

  const update = useCallback(async () => {
    if (!parent.current || !floating.current) {
      return;
    }
    const { x, y } = await computePosition(parent.current, floating.current, offset, bindToBody);
    flushSync(() => {
      setData((prev) => ({
        ...prev,
        x,
        y,
      }));
    });
  }, [strategy]);

  const debounced = debounce(update, time);

  useLayoutEffect(() => {
    window.addEventListener('scroll', debounced);
    return () => {
      window.removeEventListener('scroll', debounced);
    };
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', debounced);
    return () => {
      window.removeEventListener('resize', debounced);
    };
  }, []);

  useLayoutEffect(() => {
    // Skip first update
    if (isMounted.current) {
      update();
    }
  }, [update]);

  useLayoutEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onMountCallback = useCallback(() => {
    if (typeof cleanupRef.current === 'function') {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    update();
  }, [update]);

  const setParent = useCallback(
    (node) => {
      parent.current = node;
      onMountCallback();
    },
    [onMountCallback],
  );

  const setFloating = useCallback(
    (node) => {
      floating.current = node;
      onMountCallback();
    },
    [onMountCallback],
  );

  const refs = useMemo(() => ({ parent, floating }), []);

  return useMemo(
    () => ({
      ...data,
      update,
      refs,
      parent: setParent,
      floating: setFloating,
    }),
    [data, update, refs, setParent, setFloating],
  );
};

export default useFloat;
